import React, { useState, useEffect } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from 'dayjs';
import moment from "moment";
import axios from "axios";
import { APIURL } from "../../../config";

const shortcutsItems = [
    {
        label: 'This Week',
        getValue: () => {
            const today = dayjs();
            return [today.startOf('week'), today.endOf('week')];
        },
    },
    {
        label: 'Last Week',
        getValue: () => {
            const today = dayjs();
            const prevWeek = today.subtract(7, 'day');
            return [prevWeek.startOf('week'), prevWeek.endOf('week')];
        },
    },
    {
        label: 'Last 7 Days',
        getValue: () => {
            const today = dayjs();
            return [today.subtract(7, 'day'), today];
        },
    },
    {
        label: 'Current Month',
        getValue: () => {
            const today = dayjs();
            return [today.startOf('month'), today.endOf('month')];
        },
    },
    {
        label: 'Last Month',
        getValue: () => {
            const today = dayjs();
            const lastMonth = today.subtract(1, 'month');
            return [lastMonth.startOf('month'), lastMonth.endOf('month')];
        },
    },
    { label: 'Reset', getValue: () => [null, null] },
];

export default function UnitsDashboard(props) {
    const [customerList, setCustomerList] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [taskTypeList, setTaskTypeList] = useState([]);
    const [selectedTaskType, setSelectedTaskType] = useState(null);
    const [fromDate, setFromDate] = useState(() => dayjs().startOf('week').toDate());
    const [toDate, setToDate] = useState(() => dayjs().endOf('week').toDate());
    const [iframeUrl, setiframeUrl] = useState('');
    const [isIframeLoading, setIsIframeLoading] = useState(false);

    useEffect(() => {
        fetchCustomerData();
        fetchCompanyProcedureData();
    }, []);

    useEffect(() => {
        fetchMetabaseData();
    }, [selectedCustomer, selectedTaskType, fromDate, toDate]);

    const fetchCustomerData = async () => {
        axios
            .get(APIURL + "/customers/v2/associated", {
                params: {
                    company_id: props.user.comp_id,
                    employee_id: props.user.emp_id,
                    type: props.user.permissions.AdminLevelCustomers ? "Admin" : "Manager"
                },
            })
            .then((response) => {
                setCustomerList(response?.data.filter((cus) => cus.cus_status === 'Active') || []);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const fetchCompanyProcedureData = async () => {
        axios
            .get(APIURL + "/dropdowns/dropdownType/taskTypeList", {
                params: {
                    comp_id: props.user.comp_id
                },
            })
            .then((response) => {
                setTaskTypeList(response.data.filter((taskType) => [0, 3].includes(taskType.is_Active)));
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const fetchMetabaseData = async () => {
        setIsIframeLoading(true);

        axios
            .get(APIURL + "/metabase/unitsDashboard", {
                params: {
                    companyID: props.user.comp_id,
                    companyName: props.user.company_name,
                    customerID: selectedCustomer?.cus_id ?? null,
                    customerName: selectedCustomer?.cus_name ?? null,
                    procedure: selectedTaskType?.EntityValue ?? null,
                    fromDate: fromDate ? moment(fromDate).format('YYYY-MM-DD') : null,
                    toDate: toDate ? moment(toDate).format('YYYY-MM-DD') : null,
                },
            })
            .then((response) => {
                setiframeUrl(response.data);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const handleIframeLoad = () => {
        setIsIframeLoading(false);
    };

    return (
        <div>
            <div style={{ display: 'flex', marginLeft: 10, marginRight: 10, marginTop: 10, width: '100%' }}>
                <Autocomplete
                    id="client-select"
                    options={customerList}
                    getOptionLabel={(option) => option.cus_name}
                    onChange={(event, value) => setSelectedCustomer(value)}
                    renderOption={(props, option) => (
                        <li {...props} key={option.cus_id}>
                            {option.cus_name}
                        </li>
                    )}
                    renderInput={(params) => <TextField {...params} label="Select Client" />}
                    sx={{ width: 250, marginRight: 2 }}
                    size="small"
                />
                <Autocomplete
                    id="procedure-select"
                    options={taskTypeList}
                    getOptionLabel={(option) => option.EntityValue}
                    onChange={(event, value) => setSelectedTaskType(value)}
                    renderOption={(props, option) => (
                        <li {...props} key={option.ddID}>
                            {option.EntityValue}
                        </li>
                    )}
                    renderInput={(params) => <TextField {...params} label="Select Procedure" />}
                    sx={{ width: 250, marginRight: 2 }}
                    size="small"
                />
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateRangePicker
                        label='Date Range'
                        defaultValue={shortcutsItems[0].getValue()}
                        onChange={(newValue) => {
                            setFromDate(newValue[0] ? new Date(newValue[0]) : null);
                            setToDate(newValue[1] ? new Date(newValue[1]) : null);
                        }}
                        slotProps={{
                            shortcuts: {
                                items: shortcutsItems,
                            },
                            textField: { size: 'small', style: { width: 275 } },
                        }}
                    />
                </LocalizationProvider>
            </div>
            <div style={{ marginLeft: 10, marginRight: 10, width: '100%' }}>
                <iframe
                    src={iframeUrl}
                    style={{ width: '100%', height: '75vh', border: 'none', backgroundColor: 'transparent', colorScheme: 'light' }}
                    allowTransparency
                    onLoad={handleIframeLoad}
                />
            </div>
        </div>
    );
};